/* eslint-disable arrow-body-style */

import AutoSaveModal from '@gp/components/AutoSaveModal';
import CardSlot from '@gp/components/CardSlot';
import Loading from '@gp/components/Loading';
import SearchCommon from '@gp/components/SearchCommon';
import { SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE } from '@gp/constants/classNames';
import {
  FILE_TYPES_MAP,
  SIMPLE_DOWNLOAD_FILE_TYPES1,
} from '@gp/constants/const';
import { KEYS_MAPPING } from '@gp/constants/field';
import {
  EDIT_PAGE_KEY_MAP,
  useAutoSaveParams,
} from '@gp/hooks/useAutoSaveParams';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import usePageFilterInit from '@gp/hooks/usePageFilterInit';
import usePageLog from '@gp/hooks/usePageLog';
import { formatSearchParams } from '@gp/pages/Pipeline/utils';
import {
  downloadProjectGRActionItems,
  fetchProjectGRActionItemsProjects,
} from '@gp/services/grActionItems';
import type {
  IRecord,
  ISearchPayload,
  ISearchPayloadPageStore,
} from '@gp/types/global';
import type {
  IProjectGRActionItemProjectDTO,
  IProjectGRActionItemProjectVO,
  IProjectGRActionItemsFilterConditionsVO,
  IProjectGRActionItemsFilterVO,
} from '@gp/types/grActionItems';
import { getSortByWhenHasUniqueSortKeys } from '@gp/utils/common';
import {
  formatSearchProjectsResults,
  toProjectDetails,
} from '@gp/utils/project';
import { deleteFilterAllValue } from '@gp/utils/utils';
import MultiTypeDownloadBtn from '@intelligence/pages/TopCharts/components/MultiTypeDownloadBtn';
import { useBoolean, useDeepCompareEffect } from 'ahooks';
import classnames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Justify, Layout } from 'tea-component';
import type { SortBy } from 'tea-component/lib/table/addons';
import useDomainPageStore from './_hooks/useDomainPageStore';
import useGRActionsFilterConditions from './_hooks/useGRActionsFilterConditions';
import EditGRActionItemsFormModal from './components/EditGRActionItemsFormModal';
import Filter from './components/Filter';
import GRActionTableCard from './components/GRActionTableCard';
import './index.scss';
import { DEFAULT_FILTER, DEFAULT_SEARCH, DEFAULT_SORT } from './models/locker';
import {
  formatFilterFetchParams,
  formatSortFetchParams,
  getSearchList,
} from './utils';

const { Content } = Layout;

// 具有唯一性排序的排序字段（即集合中的排序字段，同时仅允许一个字段排序）
const UNIQUE_SORT_KEYS = ['project', 'gate_review'];

const ProjectGRActionItems: React.FC = () => {
  const headerTitle = 'GR Action Items';
  const currentPage = 'pipelineGRActionItems';
  const searchPlaceholder = 'Search any project in the table';
  const showRightBtn = true;

  const { isDownloading, handleDownload } = useDownload({
    csvDownloadLogKey: 'PIPELINE_GR_ACTION_ITEMS_DOWNLOAD_CSV_SUBMIT',
    excelDownloadLogKey: 'PIPELINE_GR_ACTION_ITEMS_DOWNLOAD_EXCEL_SUBMIT',
    downloadService: createDownloadService(downloadProjectGRActionItems),
  });

  const [isShowAll, { toggle: toggleIsShowAll }] = useBoolean(false);

  const filterHeightRef = useRef(null);
  const scrollBody = useRef(null);
  // const [tableHeight, setTableHeight] = useState(562);
  const {
    loading: filterConditionsLoading,
    values: filterValues,
    conditions: filterConditions,
    refreshConditions,
    defaultValues: allValuesInterface,
    actionItemCategory,
  } = useGRActionsFilterConditions();

  const {
    lock,
    filter: lockerFilter,
    sort: lockerSort,
    search: lockerSearch,
  } = useDomainPageStore({
    getFilter: () => filter,
    getSort: () => sort,
    getSearch: () => search,
  });

  const [autoSaveVisible, setAutoSaveVisible] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isUseAutoSaveDatas, setIsUseAutoSaveDatas] = useState(false);
  const [editData, setEditData] =
    useState<IProjectGRActionItemProjectDTO | null>(null);
  const [selectedValue, setSelectedValue] = useState<any>({});
  // const { visible, setVisible, editData, onEdit, setAutoSaveVisible } = useGRActionItemsEdit();

  const fields = [
    {
      key: 'dueDate',
      type: 'rangePicker',
      saveKey: 'gr_dueDate',
    },
    {
      key: KEYS_MAPPING.CASCADE_PROJECT,
      type: 'cascadeProject',
    },
    { key: 'gate_review', type: 'selectMultiple', label: 'Review Type' },
    { key: 'owner', type: 'selectMultiple', label: 'Owner' },
    {
      key: 'status',
      type: 'selectMultiple',
      label: 'Status',
      saveKey: 'gr_status',
    },
    { key: 'stage', type: 'selectMultiple', label: 'Stage' },
  ];

  const {
    loading: initialLoading,
    filter: initialFilterTemp,
    onUpdateShortUrlParams,
  } = usePageFilterInit({
    loading: filterConditionsLoading,
    conditions: {
      filter: filterConditions,
    },
    storeValues: {
      lock,
      filter: lockerFilter,
    },
    defaultValues: {
      filter: {
        ...DEFAULT_FILTER,
        status: filterValues?.status || [],
        stage: filterValues?.stage || [],
        project_id: filterValues?.project_id || [],
      },
    },
    allFilterOptionsValues: allValuesInterface,
    fields,
    setSelectedValue,
  });

  const defaultValuesCur = useMemo(() => {
    return {
      ...DEFAULT_FILTER,
      status: filterValues?.status || [],
      stage: filterValues?.stage || [],
      project_id: filterValues?.project_id || [],
    };
  }, [DEFAULT_FILTER, filterValues]);

  const [editAutoSaveData, setEditAutoSaveData] = useState(editData);

  useDeepCompareEffect(() => {
    setEditAutoSaveData(editData);
  }, [editData]);

  const pageKeyCur = useMemo(() => {
    return `${EDIT_PAGE_KEY_MAP.pipeline_GRAction_items_edit}_${editData?.project_id}`;
  }, [editData]);

  // 自动保存hooks
  const { getSaveParamsValue, showRestorePrompt, deleteSaveParamsByPageKey } =
    useAutoSaveParams({
      pageKey: pageKeyCur,
    });

  const handleAutoSaveConfirm = () => {
    const newValue = getSaveParamsValue(pageKeyCur) || {};
    setEditAutoSaveData(newValue);
    setIsUseAutoSaveDatas(true);
    setAutoSaveVisible(false);
    setVisible(true);
  };

  const handleAutoSaveCancel = () => {
    deleteSaveParamsByPageKey(pageKeyCur);
    setEditAutoSaveData(editData);
    setIsUseAutoSaveDatas(false);
    setAutoSaveVisible(false);
    setVisible(true);
  };

  const handleEdit = (payload?: IProjectGRActionItemProjectDTO | null) => {
    const { edit: editablePermission } = payload || {};
    if (!!editablePermission) {
      setEditData(payload || null);

      if (
        showRestorePrompt(
          `${EDIT_PAGE_KEY_MAP.pipeline_GRAction_items_edit}_${payload?.project_id}`,
        )
      ) {
        setAutoSaveVisible(true);
      } else {
        setVisible(true);
        setIsUseAutoSaveDatas(false);
      }
    }
  };

  // GRActionItems的日期筛选器特殊处理，继续过来的值，dateType是固定的date
  const initialFilter = useMemo(() => {
    return {
      ...initialFilterTemp,
      dueDate: {
        ...initialFilterTemp?.dueDate,
        dateType: 'date',
      },
    };
  }, [initialFilterTemp]);

  const [filter, setFilter] = useState<IProjectGRActionItemsFilterVO>(
    initialFilter as any,
  );

  const [sort, setSort] = useState<SortBy<IProjectGRActionItemProjectVO>[]>(
    lock ? lockerSort : DEFAULT_SORT,
  );

  const [search, setSearch] = useState<ISearchPayloadPageStore>(
    lock ? lockerSearch : DEFAULT_SEARCH,
  );

  const [grActionItemsProjectsLoading, setGRActionItemsProjectsLoading] =
    useState(true);
  const [grActionItemsProjectsError, setGRActionItemsProjectsError] = useState<
  Error | undefined
  >(undefined);
  const [grActionItemsProjects, setGRActionItemsProjects] = useState<
    IProjectGRActionItemProjectDTO[]
  >([]);

  const updateGRActionItemsProjects = async (
    filterPayload: IProjectGRActionItemsFilterVO = filter,
    filterConditionsPayload: IProjectGRActionItemsFilterConditionsVO = filterConditions,
    sortPayload: SortBy<IProjectGRActionItemProjectVO>[] = sort,
    searchPayload: ISearchPayloadPageStore = search,
  ) => {
    try {
      const filterTmp = { ...filter, ...filterPayload };
      const filterParams = formatFilterFetchParams(
        filterTmp,
        filterConditionsPayload,
      );
      const sortParams = formatSortFetchParams(sortPayload);
      const searchParams = formatSearchParams(searchPayload);
      const params = {
        ...filterParams,
        ...sortParams,
        ...searchParams,
      };
      setFilter(filterTmp);
      setGRActionItemsProjectsLoading(true);
      const res = await fetchProjectGRActionItemsProjects(params);
      setGRActionItemsProjects(res?.data || []);
      setGRActionItemsProjectsError(undefined);
    } catch (exception: any) {
      setGRActionItemsProjects([]);
      setGRActionItemsProjectsError(exception);
    } finally {
      setGRActionItemsProjectsLoading(false);
    }
  };

  const handleFilterChange = (payload?: IProjectGRActionItemsFilterVO) => {
    const filterTmp = { ...filter, ...payload };
    setFilter(filterTmp);
    updateGRActionItemsProjects(filterTmp);
    onUpdateShortUrlParams({ filter: filterTmp });
  };

  const handleSortChange = (
    payload?: SortBy<IProjectGRActionItemProjectVO>[],
  ) => {
    const payloadTmp = getSortByWhenHasUniqueSortKeys(
      UNIQUE_SORT_KEYS,
      payload,
    );
    setSort(payloadTmp);
    updateGRActionItemsProjects(filter, filterConditions, payloadTmp);
  };

  const handleSearchSubmit = (
    payload: ISearchPayload,
    searchList: IRecord<any>[],
  ) => {
    const {
      search_type: searchType,
      search_keyword: searchKeyword,
      search,
      search_ids: searchIds,
    } = payload || {};
    const searchTmp = {
      search_type: searchType || '',
      search_keyword: searchKeyword || '',
      search: search || [],
      search_ids: searchIds || [],
      search_list: searchList || [],
    };
    setSearch(searchTmp);
    updateGRActionItemsProjects(filter, filterConditions, sort, searchTmp);
  };

  const handleRetry = () => {
    updateGRActionItemsProjects();
  };

  const handleReset = () => {
    const searchTmp = lock ? lockerSearch : DEFAULT_SEARCH;
    const defValue = {
      ...defaultValuesCur,
      dueDate: initialFilter?.dueDate || defaultValuesCur?.dueDate || {},
    };
    setSearch(searchTmp);
    updateGRActionItemsProjects(defValue, filterConditions, sort, searchTmp);
    onUpdateShortUrlParams({ filter: defValue });
    setSelectedValue({});
    deleteFilterAllValue();
  };

  const setSearchListHandle = (list: any) => {
    const searchDataTmp = {
      ...search,
      search_list: list || [],
    };
    setSearch(searchDataTmp);
  };
  const setSearchValHandle = (val: any) => {
    const searchDataTmp = {
      ...search,
      search_keyword: val || '',
    };
    setSearch(searchDataTmp);
  };

  const handleDownloadInPage = (fileType?: string) => {
    const fileTypeTmp = fileType || FILE_TYPES_MAP.CSV;
    const filterParams = formatFilterFetchParams(filter, filterConditions);
    const sortParams = formatSortFetchParams(sort);
    const searchParams = formatSearchParams(search);
    handleDownload({
      ...filterParams,
      ...sortParams,
      ...searchParams,
      fileType: fileTypeTmp,
      file_type: Number.parseInt(fileTypeTmp, 10),
    });
  };

  const handleRefresh = async () => {
    setGRActionItemsProjectsLoading(true);
    const { values, conditions } = await refreshConditions();
    const { dueDate, owner, status } = values || {};
    const filterTmp = {
      ...filter,
      dueDate: { ...filter?.dueDate, ...dueDate },
      owner: owner || [],
      status: status || [],
    };
    deleteSaveParamsByPageKey(pageKeyCur);
    updateGRActionItemsProjects(filterTmp, conditions);
    onUpdateShortUrlParams({ filter: filterTmp });
    setFilter(filterTmp);
  };

  const onEditCancelHandle = () => {
    setEditData(null);
    deleteSaveParamsByPageKey(pageKeyCur);
  };

  useEffect(() => {
    if (initialLoading) return;
    updateGRActionItemsProjects(initialFilter as any, filterConditions);
    onUpdateShortUrlParams({ filter: initialFilter });
  }, [initialLoading]);

  // 页面打点
  usePageLog({ pageId: 'PIPELINE_GR_ACTION_ITEMS' });

  // 刷新搜索组件弹出面板位置的依赖数组
  const searchSelectUpdateDeps = useMemo<React.DependencyList>(() => [], []);

  return (
    <>
      {filterConditionsLoading ? (
        <Loading loading />
      ) : (
        <Content
          className={classnames(
            'dbg-common-page dbg-pipeline-gr-action-items dbg-pipeline-table-card-body-bottom0px dbg-page-scroll-body',
            SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE,
          )}
          ref={scrollBody}
        >
          <Content.Header
            title={headerTitle}
            className="dbg-common-page__header"
          />
          <Content.Body full className="dbg-common-page__body">
            <Justify
              className="dbg-common-page__toolbar-justify"
              right={
                <div className="dbg-common-page__toolbar">
                  <SearchCommon
                    placeholder={searchPlaceholder}
                    showRightBtn={showRightBtn}
                    currentPage={currentPage}
                    updateDeps={searchSelectUpdateDeps}
                    submit={handleSearchSubmit}
                    getSearchList={(payload: any) => {
                      return getSearchList(payload, {
                        sort,
                      });
                    }}
                    formatSearchResults={formatSearchProjectsResults}
                    searchVal={search?.search_keyword}
                    searchList={search?.search_list}
                    onRightBtnClick={(payload: any, isJump = true) => {
                      const { project_id: id, name } = payload || {};
                      const params = { id, name, tab: 'gameDetail' };
                      return toProjectDetails(params, null, isJump);
                    }}
                    setSearchList={setSearchListHandle}
                    setSearchVal={setSearchValHandle}
                  />
                  <MultiTypeDownloadBtn
                    isDisabled={
                      grActionItemsProjectsLoading ||
                      !grActionItemsProjects?.length
                    }
                    isLoading={isDownloading}
                    options={SIMPLE_DOWNLOAD_FILE_TYPES1}
                    onSelect={handleDownloadInPage}
                  />
                </div>
              }
            />

            <Filter
              loading={filterConditionsLoading}
              data={filterConditions}
              value={filter}
              onChange={handleFilterChange}
              allFilterOptionsValues={allValuesInterface}
              onReset={handleReset}
              scrollBody={scrollBody}
              headerTitle={headerTitle}
              showRightBtn={showRightBtn}
              currentPage={currentPage}
              updateDeps={searchSelectUpdateDeps}
              handleSearch={handleSearchSubmit}
              formatSearchResults={formatSearchProjectsResults}
              searchVal={search?.search_keyword}
              searchList={search?.search_list}
              selectedValue={selectedValue}
              setSelectedValue={setSelectedValue}
              getSearchList={(payload: any) => {
                return getSearchList(payload, {
                  sort,
                });
              }}
              setSearchList={setSearchListHandle}
              setSearchVal={setSearchValHandle}
            />

            <CardSlot className="dbg-pipeline-gr-action-card" rightDom={null}>
              <GRActionTableCard
                className="dbg-common-page__tables dbg-table-sticky"
                loading={grActionItemsProjectsLoading}
                error={grActionItemsProjectsError}
                isShowAll={isShowAll}
                data={grActionItemsProjects}
                sort={sort}
                onSortChange={handleSortChange}
                onRetry={handleRetry}
                onReset={handleReset}
                onEdit={handleEdit}
                toggleIsShowAll={toggleIsShowAll}
              />
            </CardSlot>
          </Content.Body>
        </Content>
      )}
      <EditGRActionItemsFormModal
        visible={visible}
        setVisible={setVisible}
        data={editAutoSaveData}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onEditSuccess={handleRefresh}
        onEditCancel={onEditCancelHandle}
        pageKeyCur={pageKeyCur}
        isUseAutoSaveDatas={isUseAutoSaveDatas}
        actionItemCategory={actionItemCategory}
      />

      {/* 咨询自动保存弹窗 */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />
    </>
  );
};

export default ProjectGRActionItems;
